import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import ErrorState from "../../components/ui/ErrorState";
import ScopePicker from "../../components/ui/ScopePicker";
import type { Viewer } from "../../services/viewer";
import SingleSensorPicker from "../../components/ui/SingleSensorPicker";
import Spinner from "../../components/ui/Spinner";
import { getSensorGroups } from "../../pages/Events/unitEventsUtils";
import {
  draftFromGrant,
  draftToGrantBody,
  grantsNothing,
  type GrantDraft,
} from "../../services/grantDraft";
import {
  GRANTS_QUERY_KEY,
  apiMessage,
  MEMBERS_QUERY_KEY,
  addMemberRoles,
  putAccessGrant,
  type AccessGrant,
  removeMember,
  removeMemberRoles,
  useAccessGrants,
  useMembers,
} from "../../services/people";
import { assignableRoles, tierForEditor, useRoleCatalogue } from "../../services/roles";

/**
 * One person: the role they hold, and which sensors they can reach.
 *
 * Roles and access are saved separately, because they are separate API calls
 * against separate systems -- roles live in Auth0, grants in DynamoDB -- and a
 * single Save implying they succeed or fail together would be a lie. Each section
 * reports its own outcome.
 */
export default function MemberDetailPage() {
  // AdminLayout has already fetched and gated on the viewer, so it comes down the
  // outlet rather than being fetched again here.
  const { viewer } = useOutletContext<{ viewer: Viewer }>();

  const { userId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const members = useMembers();
  const grants = useAccessGrants();
  const catalogue = useRoleCatalogue();

  const member = (members.data ?? []).find((m) => m.userId === userId);
  const grant = (grants.data ?? []).find((g) => g.user_id === userId);

  /**
   * The organization's properties, for the picker.
   *
   * Scoped server-side to what the caller may see -- which for an admin is all of
   * them, since admins are not scoped. A non-admin never reaches this page.
   */
  const properties = useQuery({
    queryKey: ["admin", "properties"],
    queryFn: async () => {
      const response = await getSensorGroups();
      return ((response?.data ?? []) as { id: string; name: string }[]).map((g) => ({
        buildingId: g.id,
        name: g.name,
      }));
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Seeded once the grant arrives and never re-seeded afterwards: the same
  // contract UserConfigEditor states, for the same reason. Re-seeding would throw
  // away whatever the admin had ticked the moment a refetch resolved.
  const [draft, setDraft] = useState<GrantDraft | null>(null);
  useEffect(() => {
    if (draft === null && !grants.isPending) setDraft(draftFromGrant(grant));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grants.isPending]);

  const [roleId, setRoleId] = useState<string | null>(null);
  const roles = assignableRoles(catalogue.data);

  /** The member's current assignable role, if they hold one. */
  const currentRoleId = useMemo(() => {
    const held = (member?.roles ?? []).map((r) => r.name);
    return roles.find((r) => held.includes(r.name))?.id ?? null;
  }, [member, roles]);

  useEffect(() => {
    if (roleId === null && currentRoleId !== null) setRoleId(currentRoleId);
  }, [currentRoleId, roleId]);

  /**
   * The tier the access editor should follow.
   *
   * The SELECTED role, not the saved one, so picking "User" switches the editor
   * immediately. That ordering matters: a User's grant must already be one sensor
   * before the role lands (REQ-ACL-15 refuses the demote otherwise), so the editor
   * has to offer the single-sensor control while the admin still holds the old
   * role -- which is exactly when the selection differs from what is saved.
   *
   * With nothing selected it falls back to `member.tier`, the API's own fold, and
   * NOT to the assignable catalogue alone. Two ways a real User reached the WIDER
   * editor through that lookup:
   *
   *  - the catalogue is a separate query and this card does not wait on it, so
   *    while it was pending -- or had failed outright -- `roles` was empty, nothing
   *    matched, and a User was handed ScopePicker. Long enough to tick whole
   *    properties into a grant the server then refuses.
   *  - `assignableRoles` drops every role a tenant admin cannot hand out, so a
   *    person holding a family or internal role matched nothing even once it had
   *    loaded. Their tier is still User and the API still holds them to one sensor.
   *
   * `member.tier` sits on the member row this page has already waited for, so the
   * first render is right rather than corrected a moment later.
   */
  const selectedTier = useMemo(
    () =>
      tierForEditor({
        selectedRoleId: roleId,
        assignable: roles,
        heldRoleNames: (member?.roles ?? []).map((r) => r.name),
        memberTier: member?.tier,
      }),
    [roles, roleId, member]
  );

  const isUserTier = selectedTier === "User";

  /**
   * Whether the old role was already taken away when a role change failed.
   *
   * Two calls, not one, so there is a state in between: the removal lands and the
   * addition is refused. The fallback wording said "We couldn't change the role.
   * Nothing was saved." -- which in that state is untrue in the direction that
   * matters, because the person now holds NO role and can do nothing, and the
   * screen has just told their admin to stop worrying about it.
   */
  const [roleRemoved, setRoleRemoved] = useState(false);

  const saveRole = useMutation({
    mutationFn: async (nextRoleId: string) => {
      setRoleRemoved(false);

      // Remove the old one first: these roles are alternatives, not additive, and
      // leaving both would give the union of their capabilities -- which for
      // OrgAdmin plus Monitor silently means the admin never lost anything. The
      // cost of that ordering is the half-applied state above; it is still the
      // right way round, because failing this way leaves somebody with less
      // access than intended rather than more.
      if (currentRoleId && currentRoleId !== nextRoleId) {
        await removeMemberRoles(userId, [currentRoleId]);
        setRoleRemoved(true);
      }
      await addMemberRoles(userId, [nextRoleId]);
      setRoleRemoved(false);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY }),
    onError: () => queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY }),
  });

  const remove = useMutation({
    mutationFn: async () => await removeMember(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
      navigate("/admin/people");
    },
  });

  const saveAccess = useMutation({
    mutationFn: async (next: GrantDraft) =>
      putAccessGrant(userId, draftToGrantBody(next)),
    // The RESPONSE is written into the cache, not a refetch triggered from here.
    // The write endpoint returns the stored grant, which is authoritative; a
    // refetch is a second read racing a write that may not have replicated yet,
    // and losing that race presents as the save not sticking -- the button stays
    // offered and no confirmation appears, with nothing actually wrong.
    //
    // The server now reads this list consistently too, so the race is closed at
    // both ends. Using the response is still right: it is one fewer round trip
    // and it cannot disagree with what was just stored.
    onSuccess: (saved) => {
      queryClient.setQueryData<AccessGrant[]>(GRANTS_QUERY_KEY, (old) => [
        ...(old ?? []).filter((g) => g.user_id !== saved.user_id),
        saved,
      ]);
    },
  });

  if (members.isPending || grants.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  if (!member) {
    return (
      <ErrorState
        title="We couldn't find that person"
        message="They may have been removed from this organization."
        onRetry={() => navigate("/admin/people")}
      />
    );
  }

  const roleChanged = roleId !== null && roleId !== currentRoleId;
  const accessChanged =
    draft !== null &&
    JSON.stringify(draftToGrantBody(draft)) !==
      JSON.stringify(draftToGrantBody(draftFromGrant(grant)));

  return (
    <>
      <nav aria-label="Breadcrumb" className="mb-5">
        <button
          type="button"
          onClick={() => {
            if (
              (roleChanged || accessChanged) &&
              !window.confirm(
                "Discard your unsaved changes?\n\n" +
                  (roleChanged && accessChanged
                    ? "You've changed this person's role and their sensor access, and neither is saved yet."
                    : roleChanged
                      ? "You've changed this person's role but haven't saved it yet."
                      : "You've changed this person's sensor access but haven't saved it yet.")
              )
            ) {
              return;
            }
            navigate("/admin/people");
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-chip border border-primary/45 bg-primary/15 text-primary hover:bg-primary/25"
        >
          <ArrowBackIosIcon fontSize="small" />
          <span className="font-semibold">Back to People</span>
        </button>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white">
          {member.name || member.email || member.userId}
        </h1>
        {member.email && member.name && (
          <p className="text-ink-3 mt-1">{member.email}</p>
        )}
      </div>

      <div className="space-y-4">
        <Card tone="light">
          <h2 className="text-lg font-semibold mb-1">Role</h2>
          <p className="text-ink-2 text-sm mb-4">
            What this person can do. Which sensors they can do it to is set below.
          </p>

          {catalogue.isPending ? (
            <Spinner size={32} />
          ) : catalogue.isError ? (
            <p className="text-alert-warning text-sm">
              We couldn't load the available roles, so the role can't be changed
              right now.
            </p>
          ) : (
            <div className="space-y-2">
              {roles.map((role) => (
                <label key={role.id} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="role"
                    className="mt-1 h-4 w-4 accent-[#BBDA62]"
                    checked={roleId === role.id}
                    onChange={() => setRoleId(role.id)}
                  />
                  <span>
                    {/* The LABEL, not role.name. Auth0's name is an
                        authorization identifier -- it still travels on the wire
                        and still identifies which role somebody holds -- but
                        "OrgAdmin" means nothing to a customer's admin. */}
                    <span className="font-medium">{role.label || role.name}</span>
                    <span className="block text-ink-2 text-sm">{role.summary}</span>
                  </span>
                </label>
              ))}

              <div className="flex items-center gap-3 pt-2">
                <Button
                  size="sm"
                  disabled={!roleChanged || saveRole.isPending}
                  onClick={() => roleId && saveRole.mutate(roleId)}
                >
                  {saveRole.isPending ? <Saving /> : "Save role"}
                </Button>
                {saveRole.isSuccess && !roleChanged && (
                  <span className="text-alert-success text-sm">Role saved.</span>
                )}
                {saveRole.isError && (
                  <span className="text-alert-critical text-sm">
                    {apiMessage(
                      saveRole.error,
                      roleRemoved
                        ? "We couldn't give them the new role."
                        : "We couldn't change the role. Nothing was saved."
                    )}
                    {/* Said whatever the server's own wording was, because this
                        is not a detail of the failure -- it is the state the
                        person is in now, and the only thing their admin has to
                        act on. */}
                    {roleRemoved && (
                      <>
                        {" "}
                        Their previous role has already been taken away, so they
                        hold no role until this succeeds. Choose a role and save
                        again.
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
          )}
        </Card>

        <Card tone="light">
          <h2 className="text-lg font-semibold mb-1">Sensor access</h2>
          {/* The property advice is wrong for a User, who has no properties and
              whose own control says what it needs. Leaving it would tell them to
              choose whole properties immediately above a control that cannot. */}
          {!isUserTier && (
            <p className="text-ink-2 text-sm mb-4">
              Which properties this person can see and act on. Choose whole
              properties where you can — a sensor moved to another property follows
              that property's access, so individual choices need revisiting after a
              move.
            </p>
          )}

          {properties.isError ? (
            <p className="text-alert-warning text-sm">
              We couldn't load your properties, so access can't be changed right now.
            </p>
          ) : properties.isPending || draft === null ? (
            <Spinner size={32} />
          ) : (
            <>
              {isUserTier ? (
                <SingleSensorPicker
                  value={draft}
                  properties={properties.data ?? []}
                  onChange={setDraft}
                  disabled={saveAccess.isPending}
                />
              ) : (
                <ScopePicker
                  value={draft}
                  onChange={setDraft}
                  properties={properties.data ?? []}
                  disabled={saveAccess.isPending}
                />
              )}

              {grantsNothing(draft) && (
                <p className="text-alert-warning text-sm mt-4">
                  {/* Tense follows the tenant. Saying "once your organization
                      enforces" to a tenant that already does reads as a warning
                      about the future at the one moment the consequence is
                      immediate -- and "your organization enforces" describes a
                      setting somebody switched on, not something an organization
                      does. */}
                  {viewer?.scope_enforced
                    ? "This grants no access at all — this person can sign in and will see no sensors."
                    : "This grants no access at all. It changes nothing today, and this person will see no sensors as soon as per-person access is switched on for this organization."}
                </p>
              )}

              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-black/10">
                <Button
                  size="sm"
                  disabled={
                    !accessChanged ||
                    saveAccess.isPending ||
                    // A User's grant must name exactly one sensor, so an empty one
                    // is refused by the server. Disabling here means the admin is
                    // told before they wait for a round trip to say no.
                    (isUserTier && draft.includeSensors.length === 0)
                  }
                  onClick={() => saveAccess.mutate(draft)}
                >
                  {saveAccess.isPending ? <Saving /> : "Save access"}
                </Button>
                {saveAccess.isSuccess && !accessChanged && (
                  <span className="text-alert-success text-sm">Access saved.</span>
                )}
                {saveAccess.isError && (
                  <span className="text-alert-critical text-sm">
                    {apiMessage(
                      saveAccess.error,
                      "We couldn't save the access. Nothing was changed."
                    )}
                  </span>
                )}
              </div>
            </>
          )}
        </Card>
        <Card tone="light">
          <h2 className="text-lg font-semibold mb-1">Remove from this organization</h2>
          <p className="text-ink-2 text-sm mb-4">
            {member.name || member.email || "This person"} keeps their sign-in and
            loses access to this organization. Their sensor access is kept, so
            adding them back restores it.
          </p>

          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="danger"
              disabled={remove.isPending}
              onClick={() => {
                // A browser confirm, as BuildingUserConfigModal uses for the same
                // class of irreversible-ish action. Naming the person rather than
                // asking "are you sure?" is what makes it readable at the moment
                // somebody is clicking quickly.
                const who = member.name || member.email || userId;
                if (window.confirm(`Remove ${who} from this organization?`)) {
                  remove.mutate();
                }
              }}
            >
              {remove.isPending ? <Saving /> : "Remove"}
            </Button>

            {remove.isError && (
              <span className="text-alert-critical text-sm">
                {apiMessage(
                  remove.error,
                  "We couldn't remove them. Nothing was changed."
                )}
              </span>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

/**
 * What a save button says while it is waiting.
 *
 * A word swap alone was not enough. These calls are not fast -- the server checks
 * the caller may manage this person, which reads their roles, and the single-sensor
 * rule resolves the grant against the tenant's properties -- so the wait runs to
 * seconds, and "Saving…" in place of "Save access" on a small button is easy to miss
 * entirely. A still button reads as a stuck one, and the honest signal for "this is
 * working" is movement.
 */
function Saving() {
  return (
    <span className="inline-flex items-center justify-center gap-2 leading-none">
      {/* currentColor, so it is whatever the label is. These buttons are a lime
          fill with dark text and a red fill with white text, so a fixed colour
          would be unreadable on one of them -- and the spinner's own default,
          the brand green, is invisible on the lime one. Inheriting means a
          button added later is right without anybody revisiting this. */}
      <Spinner size={14} color="currentColor" />
      <span>Saving…</span>
    </span>
  );
}
